import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootState';
import { Fetchable, isFetched, Json, JsonObjectArray, OperationState } from '../../store/appState';
import styled from 'styled-components/macro';
import { AuthenticationMessages } from '../Post/Submission/Notifications';
import { ChildrenProps } from '../../utils/types';
import SiteHeader from '../App/SiteHeader';
import { UserRole } from '../../config/permissions';
import { RestrictedServerConfig, RestrictedSpaceConfig, RestrictedWebsiteConfig, SpaceConfigGroup, SpaceGroup } from '../../config/serverConfig';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import * as Actions from '../../store/actions';
import JsonTable from '../shared/JsonTable';
import { CompoundButton, DefaultButton } from '@fluentui/react/lib/Button';
import { INavLink, INavLinkGroup, INavStyles, Nav } from "@fluentui/react/lib/Nav";
import { History } from "history";
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import EditJsonDialog from './EditJsonDialog';
import ErrorList from './ErrorList';
import { ValidationSchema } from './JsonEditor';
import stripJsonComments from 'strip-json-comments';
import StatusDialog from './StatusDialog';
import UsersList from '../Post/Moderation/UsersList';
import ModerationList from '../Post/Moderation/ModerationList';
import { SignInState } from '../../app';
import { ResourceStrings } from '../../ResourceStrings';

const spaceColumns: IColumn[] = [
    {
        key: '1',
        name: 'Slug',
        fieldName: 'slug',
        minWidth: 120,
        maxWidth: 350,
        isRowHeader: true,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        sortAscendingAriaLabel: 'Sorted A to Z',
        sortDescendingAriaLabel: 'Sorted Z to A',
        // onColumnClick: this._onColumnClick,
        onRender: (item: RestrictedSpaceConfig) => (
            <div>/{item.slug}</div>
        ),
        data: 'string',
    },
    {
        key: '2',
        name: 'Display Name',
        fieldName: 'displayName',
        minWidth: 150,
        maxWidth: 350,
        isRowHeader: true,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        sortAscendingAriaLabel: 'Sorted A to Z',
        sortDescendingAriaLabel: 'Sorted Z to A',
        // onColumnClick: this._onColumnClick,
        data: 'string',
    },
    {
        key: '3',
        name: 'Json',
        fieldName: '',
        minWidth: 210,
        isRowHeader: true,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        sortAscendingAriaLabel: 'Sorted A to Z',
        sortDescendingAriaLabel: 'Sorted Z to A',
        // onColumnClick: this._onColumnClick,
        onRender: (item: RestrictedSpaceConfig) => (
            <div>{JSON.stringify(item)}</div>
        ),
        data: 'string',
    },
];

enum AdminRouteKeys {
    moderation = "moderation",
    onboard = "onboard",
    spaces = "spaces",
    routing = "routing",
    users = "users",
    audit = "audit",
    featureflag = "featureflag",
    pipeline = "pipeline",
    gdpr = "gdpr",
}

const navStyles: Partial<INavStyles> = {
    root: {
        width: 300,
        background: "var(--color-surface)",
        height: "100%",
    },
};

const navLinkGroups: INavLinkGroup[] = [
    {
        links: [
            {
                name: '👍 Moderation',
                url: `/admin/${AdminRouteKeys.moderation}`,
                key: AdminRouteKeys.moderation,
            }, {
                name: '🚀 Onboard',
                url: `/admin/${AdminRouteKeys.onboard}`,
                key: AdminRouteKeys.onboard,
            },
            {
                name: '🌌 Space and Site Config',
                url: `/admin/${AdminRouteKeys.spaces}`,
                key: AdminRouteKeys.spaces,
            },
            {
                name: '🌏 Routing',
                url: `/admin/${AdminRouteKeys.routing}`,
                key: AdminRouteKeys.routing,
            },
            {
                name: '🤼 Users',
                url: `/admin/${AdminRouteKeys.users}`,
                key: AdminRouteKeys.users,
            },
            {
                name: '📖 Audit Logs',
                url: `/admin/${AdminRouteKeys.audit}`,
                key: AdminRouteKeys.audit,
            },
            {
                name: '🔀 Feature Flags',
                url: `/admin/${AdminRouteKeys.featureflag}`,
                key: AdminRouteKeys.featureflag,
            },
            {
                name: '🌊 Pipeline Status',
                url: `/admin/${AdminRouteKeys.pipeline}`,
                key: AdminRouteKeys.pipeline,
            },
            {
                name: '🚦 GDPR Status',
                url: `/admin/${AdminRouteKeys.gdpr}`,
                key: AdminRouteKeys.gdpr,
            },
        ],
    },
];

const newSpacePlaceholder: string = `{
  // slug cannot be changed - choose carefully!
  "slug": "TODO", // e.g. "dotnet"

  // display name on left nav that customers see. Can be updated at any time.
  "displayName": "TODO", // e.g. ".NET"
  
  // where in Azure DevOps to put problems in this space?
  "ado": {
    // AzDo server instance. Press Ctrl+Space to list valid values
    "instance": "", // e.g. "devdiv.visualstudio.com"

    // area path to assign new or moved feedback to.
    "defaultAreaPath": "TODO" // e.g. "DevDiv\\\\Technical Insights\\\\Customer Feedback Services\\\\Testing Feedback Services"
  },

  // null if no info, otherwise space info that shows up in Report Problem/Suggest Feature views
  "info": null, // example: "For all .NET support on all platforms"

  // null to show space in navigation to everyone, otherwise group name
  "showNavTo": ["MicrosoftEmployees"], // null means "visible to all"

  // null to show contents/posts to everyone, otherwise group name
  "showContentTo": null // null means "visible to all"
}
`;

const newWebsitePlaceholder: string = `{
  // Unique key identifying the website group
  "key": "TODO", // example: "internal", "vs" or "microsoft".

  // Host names that belong to this group, usually includes the 
  // production URL as well as any staging or regional hosts
  "hostNames": [ // example: ["developercommunity.visualstudio.com", "devcom2-ppe.azurewebsites.net"]
    "TODO"
  ],
  
  // Ordered list of space slugs for spaces visible on this site
  "showSpaces": [ // example: ["VisualStudio", "VisualStudioForMac"]
    "TODO"
  ],

  // website title
  "title": "TODO" // example: "Microsoft Developer Community"
}
`;

const newFeatureFlagPlaceholder: string = `{
  // Feature Flag's name. When a new feature name is used, a new feature flag setting will be added in the db. When existing feature flag is used as name, it will override the existing value of the corresponding feature flag.
  "featureName": "TODO", // e.g. "EnableFuzzySearch"

  // Value of feature flag. It can be true/false, number, GUID, string, colon separated string etc.
  "featureValue": "TODO" // e.g. "true, false, 500,  ff110934-f6d6-4510-ae8a-2233e65ec69c, AzureDevOps, gummies:lufthansa:quickbooks:airline"
}
`;

/** Data displayed in space table */
interface SpaceViewData {
    slug: string;
    displayName: string;
    defaultAreaPath: string;
    json: string;
}

enum StatusData {
    space,
    website,
}

export default function AdminView(): JSX.Element {
    const history: History<unknown> = useHistory();
    const match = useRouteMatch();

    const signinState: SignInState = useSelector((state: RootState) => state.appState.signInState);
    const signInErrorMessage: string = useSelector((state: RootState) => state.appState.signInErrorMessage);
    const currentUserRoles: UserRole[] = useSelector((state: RootState) => state.appState.userRoles);
    const serverConfig: Fetchable<RestrictedServerConfig> | null = useSelector((state: RootState) => state.appState.serverConfig);
    const routingRules: Fetchable<Json> | null = useSelector((state: RootState) => state.appState.routingRules);
    const auditLogs: Fetchable<Json> | null = useSelector((state: RootState) => state.appState.auditLogs);
    const featureFlags: Fetchable<Json> | null = useSelector((state: RootState) => state.appState.featureFlags);
    const pipeline: Fetchable<Json> | null = useSelector((state: RootState) => state.appState.pipelineStatus);
    const gdpr: Fetchable<Json> | null = useSelector((state: RootState) => state.appState.gdprStatus);
    const modAdminUsers: Fetchable<Json> | null = useSelector((state: RootState) => state.appState.modAdminUsers);
    const updateSpaceConfig: Fetchable<any> | null = useSelector((state: RootState) => state.appState.updateSpaceConfig);
    const updateWebsiteConfig: Fetchable<any> | null = useSelector((state: RootState) => state.appState.updateWebsiteConfig);
    const userMetadataLoadState: OperationState = useSelector((state: RootState) => state.appState.userMetadataLoadState);
    const isAdmin = currentUserRoles && currentUserRoles.indexOf(UserRole.Admins) !== -1;
    const isModerator = currentUserRoles && currentUserRoles.indexOf(UserRole.Moderators) !== -1;
    const isEmployee = currentUserRoles && currentUserRoles.indexOf(UserRole.MicrosoftEmployees) !== -1;

    const path: string | null = match?.params["path"];
    const selectedKey: AdminRouteKeys = path && path !== "" ? path as unknown as AdminRouteKeys : AdminRouteKeys.moderation;

    const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
    const [showFeatureFlagDialog, setShowFeatureFlagDialog] = useState(false);
    const [showCreateSpaceDialog, setShowCreateSpaceDialog] = useState(false);
    const [showCreateWebsiteDialog, setShowCreateWebsiteDialog] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [loadingText, setLoadingText] = useState("Executing...");
    const [okText, setOkText] = useState("Success!");
    const [statusData, setStatusData] = useState(StatusData.space);
    const [spaceJson, setSpaceJson] = useState(newSpacePlaceholder);
    const [spaceData, setSpaceData] = useState([] as SpaceViewData[]);

    const [websiteJson, setWebsiteJson] = useState(newWebsitePlaceholder);
    const [websiteData, setWebsiteData] = useState([] as { key: string, hostNames: string, json: string, title: string }[]);

    useEffect(() => {
        Actions.loadExtraAdminInfo();
        Actions.loadSiteAndSpaceConfig();
    }, []);

    // recalculate space data for display
    useEffect(() => {
        if (serverConfig?.data?.spaces == null) {
            setSpaceData([]);
            return;
        }

        const data = serverConfig?.data?.spaces.map(space => {
            return {
                slug: space.slug,
                displayName: space.displayName,
                defaultAreaPath: space.ado.defaultAreaPath,
                json: JSON.stringify(space, null, 2),
            };
        });

        setSpaceData(data);
    }, [serverConfig?.data?.spaces])

    // recalculate website data for display
    useEffect(() => {
        if (serverConfig?.data?.websites == null) {
            setWebsiteData([]);
            return;
        }

        const data = serverConfig?.data?.websites.map(website => {
            return {
                key: website.key,
                hostNames: website.hostNames.join(", "),
                title: website.title,
                json: JSON.stringify(website, null, 2),
            };
        });

        setWebsiteData(data);
    }, [serverConfig?.data?.websites])

    // onboarding
    const onboardingDialog_Close = (): void => {
        setShowOnboardingDialog(false);
    }
    const onboardingDialog_Ok = (jsonWithComments: string): void => {
        setShowOnboardingDialog(false);

        const json = stripJsonComments(jsonWithComments);
        Actions.createOrEditSpaceConfig(json);

        setLoadingText("Creating space...");
        setOkText("Space created. Congratulations!");
        setStatusData(StatusData.space);
        setShowStatusDialog(true);
    }

    const onShowOnboarding = (): void => {
        setShowOnboardingDialog(true);
    }

    const onShowFeatureFlag = (): void => {
        setShowFeatureFlagDialog(true);
    }

    const onCloseFeatureFlag = (): void => {
        setShowFeatureFlagDialog(false);
    }

    const onSubmitFeatureFlag = (jsonWithComments: string): void => {
        setShowFeatureFlagDialog(false);

        const json = stripJsonComments(jsonWithComments);
        Actions.createOrEditFeatureFlag(json);

        setLoadingText("Updating Feature Flags...");
        setOkText("Feature Flag Added/Created. Congratulations!");
    }

    // space
    const createSpaceDialog_Close = (): void => {
        setShowCreateSpaceDialog(false);
    }
    const createSpaceDialog_Ok = (jsonWithComments: string): void => {
        setShowCreateSpaceDialog(false);

        const json = stripJsonComments(jsonWithComments);
        Actions.createOrEditSpaceConfig(json);

        setLoadingText("Updating space...");
        setOkText("Space updated.");
        setStatusData(StatusData.space);
        setShowStatusDialog(true);
    }

    const onShowCreateSpace = (): void => {
        setSpaceJson(newSpacePlaceholder);
        setShowCreateSpaceDialog(true);
    }

    // website
    const createWebsiteDialog_Close = (): void => {
        setShowCreateWebsiteDialog(false);
    }
    const createWebsiteDialog_Ok = (jsonWithComments: string): void => {
        setShowCreateWebsiteDialog(false);

        const json = stripJsonComments(jsonWithComments);
        Actions.createOrEditWebsiteConfig(json);

        setLoadingText("Updating website config...");
        setOkText("Website config updated.");
        setStatusData(StatusData.website);
        setShowStatusDialog(true);
    }

    const createWebsite_Show = (): void => {
        setWebsiteJson(newWebsitePlaceholder);
        setShowCreateWebsiteDialog(true);
    }

    // status
    const statusDialog_Close = (): void => {
        setShowStatusDialog(false);
    }

    // editing
    const website_Edit = (item: Json): void => {
        setWebsiteJson((item as any).json);
        setShowCreateWebsiteDialog(true);
    }

    const space_Edit = (item: any): void => {
        setSpaceJson((item as SpaceViewData).json);
        setShowCreateSpaceDialog(true);
    }

    return (
        <Wrapper>
            <SiteHeader />

            {<AuthenticationMessages
                signinState={signinState}
                signInErrorMessage={signInErrorMessage}
                render={(props: ChildrenProps): JSX.Element => <Content>{props.children}</Content>} />
            }

            {signinState === SignInState.Success && <>
                {userMetadataLoadState !== OperationState.Success && <>
                    <CenterH2>Checking permissions</CenterH2>
                </>}
                {userMetadataLoadState === OperationState.Success && !isEmployee && <>
                    <CenterH2>You are not authorized to view this page</CenterH2>
                </>}
            </>}

            {isEmployee && signinState === SignInState.Success && <Content id="page-content">
                <Nav styles={navStyles}
                    ariaLabel="Admin page navigation"
                    groups={navLinkGroups}
                    onLinkClick={(ev: React.MouseEvent<HTMLElement>, item: INavLink) => {
                        if (!item.url) { // if group header
                            return;
                        }
                        if (ev) {
                            ev.nativeEvent.preventDefault();
                        }
                        if (history?.push) {
                            history.push(item.url);
                        }
                    }}
                    selectedKey={selectedKey}
                />
                <PaneContent>
                    <div className="Privacy" aria-label="Privacy Statement" style={{
                        'backgroundColor': 'var(--color-selected-background)',
                        'fontStyle': 'italic',
                        'padding': '2rem',
                        'marginBottom': '2rem',
                        'marginTop': '2rem',
                        'flex': 'true',
                    }}>
                        <p>
                            For more details on Privacy, please see <a href="https://aka.ms/DevCominternalprivacystatement" target="_blank" rel="noopener noreferrer">Microsoft Global Data Privacy Notice for Employees, External Staff and Candidates</a>
                        </p></div>
                    {serverConfig?.errors && <>
                        <CenterH2>Error: could not fetch server configuration</CenterH2>
                        <ErrorList errors={serverConfig?.errors} />
                    </>}

                    {isFetched(serverConfig) && (serverConfig.data?.spaceErrors?.length > 0 || serverConfig.data?.websiteErrors?.length > 0) && <>
                        <div css="color: red; margin: 1rem 0; border: 0.1rem solid red; padding: 1rem; border-radius: 0.2rem">⚠ ERRORS - please check &quot;Space and Site Config&quot; section</div>
                    </>}

                    {selectedKey === AdminRouteKeys.moderation && !isAdmin && !isModerator && <>
                        <CenterH2>You are not authorized to view this page. Contact a moderator or admin to remove potentially offensive content.</CenterH2>
                    </>}
                    {selectedKey === AdminRouteKeys.moderation && (isAdmin || isModerator) && <>
                        <ModerationList />
                    </>}

                    {selectedKey === AdminRouteKeys.onboard && !isAdmin && <>
                        <CenterH2>You are not authorized to view this page. Contact the admin team for onboarding.</CenterH2>
                    </>}
                    {selectedKey === AdminRouteKeys.onboard && isAdmin && <>
                        <SectionH2>Onboard New Team</SectionH2>
                        Start receiving feedback for your product in Developer Community by following these steps:
                        <SectionH3>1. Create a space with a basic theme</SectionH3>
                        <CompoundButton css={"min-width: 20rem"} secondaryText='Such as /devbox' onClick={onShowOnboarding}>Create a space</CompoundButton>
                        <EditJsonDialog hidden={!showOnboardingDialog}
                            title="Create New Space"
                            okButtonTitle="Create Space"
                            validationSchema={ValidationSchema.SpaceConfig}
                            defaultValue={newSpacePlaceholder}
                            onClose={onboardingDialog_Close}
                            onOk={onboardingDialog_Ok}
                        />

                        <SectionH3>2. Show the space on the website</SectionH3>
                        <p>New spaces don&apos;t show up by default.</p>
                        <p>Navigate to <Link to={`/admin/${AdminRouteKeys.spaces}`}>Space and Site Config 🌌</Link> and edit Site Configuration
                            to select where your space will show up in relation to other spaces.</p>

                        <SectionH3>3. Read the welcome docs</SectionH3>
                        <p><a href="https://aka.ms/feedback/welcome" target="_blank">https://aka.ms/feedback/welcome</a></p>
                    </>}

                    {selectedKey === AdminRouteKeys.spaces && <>
                        {!isAdmin && <>
                            <CenterH2>You have read-only permissions for this page. To make updates, please contact the admin team.</CenterH2>
                        </>}
                        {isFetched(serverConfig) && <>
                            <SectionH2>Spaces</SectionH2>
                            <SpaceBottom>Configure available spaces and per-space themes and permissions.</SpaceBottom>
                            <ErrorList errors={serverConfig.data.spaceErrors} />
                            <JsonTable scrollable={false} data={spaceData as unknown as JsonObjectArray} onEdit={isAdmin ? space_Edit : null} />

                            <Space>
                                <DefaultButton css={"margin-right: 1rem"} onClick={onShowCreateSpace}>Create Space</DefaultButton>
                                <DefaultButton onClick={onShowCreateSpace}>Fix Space Config</DefaultButton>
                            </Space>
                            <EditJsonDialog hidden={!showCreateSpaceDialog}
                                title="Create/Update Space"
                                okButtonTitle="Submit"
                                validationSchema={ValidationSchema.SpaceConfig}
                                defaultValue={spaceJson}
                                onClose={createSpaceDialog_Close}
                                onOk={createSpaceDialog_Ok}
                            />

                            <SectionH2>Website Configuration</SectionH2>
                            <SpaceBottom>Determines order of spaces shown, header/footer styles and site headers and title.</SpaceBottom>
                            <ErrorList errors={serverConfig.data.websiteErrors} />
                            <JsonTable scrollable={false} data={websiteData as unknown as JsonObjectArray} onEdit={isAdmin ? website_Edit : null} />

                            <Space>
                                <DefaultButton css={"margin-right: 1rem"} onClick={createWebsite_Show}>Create Website Config</DefaultButton>
                                <DefaultButton onClick={createWebsite_Show}>Fix Website Config</DefaultButton>
                            </Space>

                            <EditJsonDialog hidden={!showCreateWebsiteDialog}
                                title="Create/Update Website Configuration"
                                okButtonTitle="Submit"
                                validationSchema={ValidationSchema.WebsiteConfig}
                                defaultValue={websiteJson}
                                onClose={createWebsiteDialog_Close}
                                onOk={createWebsiteDialog_Ok}
                            />

                        </>}
                    </>}

                    {selectedKey === AdminRouteKeys.routing && <>
                        {!isAdmin && <>
                            <CenterH2>You have read-only permissions for this page. To make updates, please contact the admin team.</CenterH2>
                        </>}
                        <SectionH2>Routing Rules</SectionH2>
                        {isFetched(routingRules) && <>
                            <SectionH3>Routing: &quot;if keyword ➡ add tag; &quot;; &quot;if tag ➡ route to path&quot;</SectionH3>
                            <JsonTable scrollable={true} data={routingRules.data.allRoutes as unknown as JsonObjectArray} />
                        </>}
                        {routingRules == null || routingRules.isFetching && <>
                            Loading...
                        </>}
                        {routingRules && routingRules.errors && <>
                            Error: {routingRules.errors}
                        </>}
                    </>}

                    {selectedKey === AdminRouteKeys.users && <>
                        {isFetched(modAdminUsers) && <>
                            <SectionH2>All Users</SectionH2>
                            {!isAdmin && !isModerator && <>
                                <p>⚠ You don&apos;t have permissions to view customer emails. Contact a moderator or admin to reach out to a customer by email.</p>
                            </>}
                            {(isModerator || isAdmin) && <UsersList />}

                            <SectionH2>Privileged Users</SectionH2>
                            <JsonTable scrollable={false} data={modAdminUsers.data as unknown as JsonObjectArray} />
                        </>}
                        {modAdminUsers == null || modAdminUsers.isFetching && <>
                            Loading...
                        </>}
                        {modAdminUsers && modAdminUsers.errors && <>
                            Error: {modAdminUsers.errors}
                        </>}
                    </>}

                    {selectedKey === AdminRouteKeys.audit && <>
                        <SectionH2>Audit Logs</SectionH2>
                        {isFetched(auditLogs) && <>
                            <SectionH3>&quot;Who did it&quot;</SectionH3>
                            <JsonTable scrollable={false} data={auditLogs.data as unknown as JsonObjectArray} />
                        </>}
                        {auditLogs == null || auditLogs.isFetching && <>
                            Loading...
                        </>}
                        {auditLogs && auditLogs.errors && <>
                            Error: {auditLogs.errors}
                        </>}
                    </>}

                    {selectedKey === AdminRouteKeys.featureflag && <>
                        <SectionH2>Feature Flags</SectionH2>
                        <FeatureFlagButton onClick={onShowFeatureFlag}>Add/Edit feature flag</FeatureFlagButton>
                        <EditJsonDialog hidden={!showFeatureFlagDialog}
                            title="Add/Edit feature flag setting"
                            okButtonTitle="Add/Edit"
                            validationSchema={ValidationSchema.FeatureFlagConfig}
                            defaultValue={newFeatureFlagPlaceholder}
                            onClose={onCloseFeatureFlag}
                            onOk={onSubmitFeatureFlag}
                        />
                        {isFetched(featureFlags) && <>
                            <JsonTable scrollable={false} data={featureFlags.data as unknown as JsonObjectArray} />
                        </>}
                        {featureFlags == null || featureFlags.isFetching && <>
                            Loading...
                        </>}
                        {featureFlags && featureFlags.errors && <>
                            Error: {featureFlags.errors}
                        </>}
                    </>}

                    {selectedKey === AdminRouteKeys.pipeline && <>
                        <SectionH2>Pipeline Status</SectionH2>
                        {isFetched(pipeline) && <>
                            <JsonTable scrollable={false} data={pipeline.data as unknown as JsonObjectArray} />
                        </>}
                        {pipeline == null || pipeline.isFetching && <>
                            Loading...
                        </>}
                        {pipeline && pipeline.errors && <>
                            Error: {pipeline.errors}
                        </>}
                    </>}

                    {selectedKey === AdminRouteKeys.gdpr && <>
                        <SectionH2>GDPR Status</SectionH2>
                        {isFetched(gdpr) && <>
                            <JsonTable scrollable={false} data={gdpr.data as unknown as JsonObjectArray} />
                        </>}
                        {gdpr == null || gdpr.isFetching && <>
                            Loading...
                        </>}
                        {gdpr && gdpr.errors && <>
                            Error: {gdpr.errors}
                        </>}
                    </>}

                    <StatusDialog hidden={!showStatusDialog}
                        data={statusData === StatusData.space ? updateSpaceConfig : updateWebsiteConfig}
                        loading={loadingText}
                        ok={okText}
                        error="An issue occurred"
                        onClose={statusDialog_Close} />
                </PaneContent>
            </Content>}
        </Wrapper >
    );
}
const SectionH2 = styled.h2`
    margin-top: 3rem;
    margin-bottom: 1rem;
`;
const SectionH3 = styled.h3`
    margin-top: 2em;
    margin-bottom: 1rem;
    font-size: 1.4rem;
`;
const ConfigPre = styled.pre`
    max-height: 40rem;
    overflow-y: auto;
    font-size: 1.2rem;
`;

const CenterH2 = styled.h2`
    margin-top: 6rem;
    text-align: center;
`;
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 40rem;
`;

const Content = styled.div`
    display: flex;
    flex-direction: row;
    min-height: 60vh;
`;

const PaneContent = styled.div`
    padding: 0 0 var(--page-margin-horizontal) var(--page-margin-horizontal);
    flex-grow: 1;
    min-width: 0; // prevent overflow width - weird, but explained here: https://stackoverflow.com/questions/36230944/prevent-flex-items-from-overflowing-a-container/66689926#66689926
`;

const OrderedList = styled.ol`
    li {
        display: list-item;
    }
`
const Space = styled.div`
    margin: 2rem 0;
`;

const SpaceBottom = styled.div`
    margin: 0 0 2rem 0;
`;

const FeatureFlagButton = styled.button`
    float: right;
    align-items: center;
    border: 1px solid gray;
    border-radius: .2rem;
    padding: 1rem 1rem;
    background: white;
    font-weight: 600;
`;